import { theme } from '@prophecy/theme';
import { isRenderableNode } from '@prophecy/utils/react/nodes';
import { forwardRef, useEffect, useState } from 'react';

import {
  StyledLoaderWrap,
  StyledTip,
  StyledSpinnerWrap,
  StyledOverlay,
  StyledLoader,
  StyledCircleIcon,
  StyledLoadingIcon
} from './styled';
import { SpinnerProps, SpinnerSize } from './types';

export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      spinning = true,
      tip,
      size = SpinnerSize.m,
      showBackgroundCircle,
      delay,
      children,
      overlayClassName,
      centered,
      showOverlayBlur = true,
      ...restProps
    },
    ref
  ) => {
    const [_spinning, setSpinning] = useState(!!spinning);

    useEffect(() => {
      // for spinning false, updating the state immediately
      if (!spinning || delay === 0) {
        setSpinning(spinning);
        return;
      }

      const timeoutId = setTimeout(() => {
        setSpinning(true);
      }, delay);

      return () => clearTimeout(timeoutId);
    }, [spinning, delay]);

    const spinner = (
      <StyledLoaderWrap alignY='center'>
        <StyledLoader $size={size}>
          <StyledLoadingIcon type='extended' size={size} spin color={theme.colors.brand600} />
          {showBackgroundCircle && <StyledCircleIcon type='default' size={size} spin color={theme.colors.white} />}
        </StyledLoader>
        {isRenderableNode(tip) && <StyledTip $size={size}>{tip}</StyledTip>}
      </StyledLoaderWrap>
    );

    let content: React.ReactNode = spinner;
    if (children) {
      content = (
        <>
          {children}
          {_spinning && (
            <StyledOverlay $showBlur={showOverlayBlur} className={overlayClassName}>
              {spinner}
            </StyledOverlay>
          )}
        </>
      );
    } else if (centered) {
      content = (
        <StyledOverlay $showBlur={showOverlayBlur} className={overlayClassName}>
          {spinner}
        </StyledOverlay>
      );
    }

    return (
      <StyledSpinnerWrap $centered={centered} {...restProps} ref={ref}>
        {content}
      </StyledSpinnerWrap>
    );
  }
);
